import { useIntl } from 'react-intl'

import { InsuranceAdvantages, InsuranceOrigin } from '@subaio/api-types/service'
import { FormattedMessage } from '@subaio/components'

import type { OnLogEvent } from 'lib/container'

import type {
	Handlers,
	OpportunityCarInsuranceStep,
	OpportunityCarInsuranceStepType,
} from 'containers/OpportunityCarInsurance/handlers'

import { ButtonText } from 'components/Buttons'
import { Card } from 'components/Card'
import { FormattedNumber } from 'components/FormattedNumber'
import { HeaderIconCelebration, IconLink } from 'components/Icons'
import { Insurance } from 'components/Insurance'
import { BottomCard, ButtonLabel, Header, HeaderIconWrapper } from 'components/Layout'

import { Title } from '../Title'
import { BottomDisclaimer } from './BottomDisclaimer'
import { Content } from './Content'

type Props = TaggedType<OpportunityCarInsuranceStep, OpportunityCarInsuranceStepType.Comparison> & {
	onShowTermsAndConditions: Handlers['onShowTermsAndConditions']
	onOpenUrl: (url: string) => void
	onLogScopedEvent: OnLogEvent
}

export const Comparison = ({
	insuranceComparison,
	selectedInsurer,
	onShowTermsAndConditions,
	onOpenUrl,
	onLogScopedEvent,
}: Props) => {
	const { formatMessage } = useIntl()

	return (
		<Card>
			{insuranceComparison.insurances
				.find((insurance) => insurance.origin.includes(InsuranceOrigin.Existing))
				?.advantages.includes(InsuranceAdvantages.Cheaper) ? (
				<>
					<Header>
						<Title>
							<FormattedMessage
								id='opportunityCarInsurance.comparison.titleAdvantage'
								defaultMessage='You are now likely to be more advantageous'
							/>
						</Title>
					</Header>
					<p role='text'>
						<FormattedMessage
							id='opportunityCarInsurance.comparison.descriptionAdvantage'
							defaultMessage='But perhaps the terms and conditions at ABN AMRO are more favorable. Compare all terms and conditions to see insurance coverages.'
						/>
					</p>
				</>
			) : (
				<>
					<Header>
						<HeaderIconWrapper>
							<HeaderIconCelebration />
						</HeaderIconWrapper>
						<Title>
							<FormattedMessage
								id='opportunityCarInsurance.comparison.title'
								defaultMessage='You may be able to save money!'
							/>
						</Title>
					</Header>
					<p role='text'>
						<FormattedMessage
							id='opportunityCarInsurance.comparison.description'
							defaultMessage='The comparison is based on the coverages you enter in advance. Compare all terms and conditions to view insurance coverages.'
						/>
					</p>
				</>
			)}
			<Content>
				{insuranceComparison.insurances.map((insurance, index) => (
					<Insurance
						key={index}
						insurance={insurance}
						onLogScopedEvent={onLogScopedEvent}
						subtitle={
							<>
								<FormattedNumber
									value={insurance.coverages.ownRisk}
									minimumFractionDigits={0}
									currency={insurance.currency}
									showCurrency
								/>
								<span aria-hidden> , - </span>
								<FormattedMessage id='opportunityCarInsurance.comparison.item.ownRisk' defaultMessage='own risk' />
							</>
						}
						onShowTermsAndConditions={() => onShowTermsAndConditions(insuranceComparison, selectedInsurer)}
						onShowSwitching={() => {
							onLogScopedEvent('CALCULATE_PREMIUM')
							onOpenUrl('https://www.abnamro.nl/mijn-abnamro/verzekeren/autoverzekering/aanvragen?pos=carefree_car')
						}}
					/>
				))}
			</Content>
			<BottomDisclaimer>
				<small>
					<FormattedMessage
						id='opportunityCarInsurance.comparison.note'
						defaultMessage='The above comparison is based on the current price and terms from poliswijzer.co.uk and may differ from your current coverage.'
					/>
				</small>
			</BottomDisclaimer>
			<BottomCard
				title={
					<FormattedMessage
						id='opportunityCarInsurance.comparison.contactTitle'
						defaultMessage='Do you have a question?'
					/>
				}
			>
				<div>
					<ButtonText
						label={
							<ButtonLabel>
								<FormattedMessage
									id='opportunityCarInsurance.comparison.labelContact'
									defaultMessage='Check the contact options and ask your question'
								/>
								<IconLink />
							</ButtonLabel>
						}
						aria-label={`
							${formatMessage({ id: 'opportunityCarInsurance.comparison.labelContact', defaultMessage: 'Check the contact options and ask your question' })}.
							${formatMessage({ id: 'buttonExternal.annotation', defaultMessage: 'You are leaving the app and will be taken to the browser.' })}
						`}
						isInline
						onClick={() => {
							onLogScopedEvent('CONTACT')
							onOpenUrl(
								'https://www.abnamro.nl/nl/prive/service-en-contact/index/path-detail/Producten/Verzekeringen.html',
							)
						}}
					/>
				</div>
			</BottomCard>
		</Card>
	)
}
